// ============================================================
//  src/pages/ContactPage.jsx
//  Updated: Google Fonts (DM Sans + Playfair Display) + Material Icons Round
// ============================================================

import { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import { Card, Input, Button } from "../components/UI";
import config from "../config";

// ── Inject Google Fonts + Material Icons ─────────────────────
function useGoogleAssets() {
  useEffect(() => {
    const links = [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700;9..40,800&family=Playfair+Display:wght@700;800&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/icon?family=Material+Icons+Round",
      },
    ];

    const injected = [];
    links.forEach(({ rel, href, crossOrigin }) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const el = document.createElement("link");
      el.rel  = rel;
      el.href = href;
      if (crossOrigin) el.crossOrigin = crossOrigin;
      document.head.appendChild(el);
      injected.push(el);
    });

    return () => injected.forEach((el) => el.remove());
  }, []);
}

// ── Material Icon helper ──────────────────────────────────────
function Icon({ name, style = {} }) {
  return (
    <span
      className="material-icons-round"
      style={{ fontSize: 20, verticalAlign: "middle", lineHeight: 1, ...style }}
    >
      {name}
    </span>
  );
}

// ── EmailJS credentials ───────────────────────────────────────
const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || "";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "";
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || "";

const INFO_CARDS = [
  { icon: "phone",          label: "Phone",    value: config.phone    },
  { icon: "email",          label: "Email",    value: config.email    },
  { icon: "location_on",    label: "Location", value: config.location },
  { icon: "schedule",       label: "Hours",    value: config.hours    },
];

// ── License row helper ────────────────────────────────────────
function LicenseRow({ label, value, mono = false, copyable = false, accentColor = config.colors.primary }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div style={{
      background: "#fff",
      borderRadius: 10,
      padding: "10px 12px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 9, color: "#bbb", fontWeight: 800,
          textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#333",
          fontFamily: mono ? "'Courier New', monospace" : "'DM Sans', sans-serif",
          letterSpacing: mono ? 1 : 0,
          wordBreak: "break-all",
        }}>
          {value}
        </div>
      </div>
      {copyable && (
        <button
          onClick={handleCopy}
          style={{
            flexShrink: 0,
            background: copied ? "#e8f5e9" : "#f5f5f5",
            border: `1px solid ${copied ? "#a5d6a7" : "#e0e0e0"}`,
            borderRadius: 8,
            padding: "5px 10px",
            fontSize: 11,
            fontWeight: 700,
            color: copied ? accentColor : "#888",
            cursor: "pointer",
            transition: "all 0.2s",
            whiteSpace: "nowrap",
            fontFamily: "'DM Sans', sans-serif",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Icon
            name={copied ? "check" : "content_copy"}
            style={{ fontSize: 13, color: copied ? accentColor : "#888" }}
          />
          {copied ? "Copied" : "Copy"}
        </button>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function ContactPage() {
  useGoogleAssets();

  const [sent, setSent]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [form, setForm]       = useState({ name: "", email: "", message: "" });

  const update = (k) => (e) => {
    setError("");
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  async function handleSend() {
    if (!form.name.trim())    return setError("Please enter your name.");
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return setError("Please enter a valid email address.");
    if (!form.message.trim()) return setError("Please enter a message.");

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setError("Email service is not configured. Please use WhatsApp or call us directly.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name:  form.name.trim(),
          from_email: form.email.trim(),
          message:    form.message.trim(),
          store_name: config.storeName,
          reply_to:   form.email.trim(),
        },
        EMAILJS_PUBLIC_KEY,
      );
      setSent(true);
    } catch (err) {
      console.error("EmailJS error:", err);
      setError("Failed to send message. Please try WhatsApp or call us directly.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      padding: "18px 13px 100px",
      maxWidth: 520,
      margin: "0 auto",
      fontFamily: "'DM Sans', sans-serif",
    }}>

      {/* ── Page title ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Icon name="call" style={{ fontSize: 26, color: config.colors.primary }} />
        <div style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 24,
          fontWeight: 800,
          color: config.colors.primary,
        }}>
          Contact Us
        </div>
      </div>
      <div style={{ color: "#bbb", fontSize: 13, marginBottom: 18, paddingLeft: 2 }}>
        We'd love to hear from you!
      </div>

      {/* ── Contact info grid ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        {INFO_CARDS.map(({ icon, label, value }) => (
          <div key={label} style={{
            background: "#fff",
            borderRadius: 13,
            padding: 13,
            boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            border: `1px solid ${config.colors.cardBorder}`,
          }}>
            <Icon name={icon} style={{ fontSize: 22, color: config.colors.primary, marginBottom: 5, display: "block" }} />
            <div style={{
              fontSize: 9, color: "#ccc", fontWeight: 800,
              textTransform: "uppercase", letterSpacing: 0.4,
              fontFamily: "'DM Sans', sans-serif",
            }}>
              {label}
            </div>
            <div style={{
              fontSize: 11, fontWeight: 700, marginTop: 2,
              color: "#444", lineHeight: 1.4,
              fontFamily: "'DM Sans', sans-serif",
            }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* ── FSSAI License Card ── */}
      <div style={{
        background: "linear-gradient(135deg, #e8f5e9, #f1f8e9)",
        border: "1.5px solid #a5d6a7",
        borderRadius: 16,
        padding: "18px 16px",
        marginBottom: 14,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 42, height: 42, minWidth: 42,
            background: config.colors.primary,
            borderRadius: 12,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="shield" style={{ fontSize: 24, color: "#fff" }} />
          </div>
          <div>
            <div style={{
              fontWeight: 800, fontSize: 15,
              color: config.colors.primary,
              fontFamily: "'DM Sans', sans-serif",
            }}>
              FSSAI License
            </div>
            <div style={{ fontSize: 11, color: "#777", marginTop: 1, fontFamily: "'DM Sans', sans-serif" }}>
              Food Safety & Standards Authority of India
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <LicenseRow label="License Number"   value={config.fssaiLicense}   mono copyable />
          <LicenseRow label="Valid Upto"        value={config.fssaiValidUpto} />
          <LicenseRow label="License Holder"   value={config.storeName}      />
          <LicenseRow label="Registered State" value={config.gstState}       />
        </div>

        <div style={{
          marginTop: 14,
          background: config.colors.primary,
          borderRadius: 10,
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <Icon name="verified" style={{ fontSize: 20, color: "#fff" }} />
          <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>
            Certified by FSSAI — Safe & hygienic food products
          </span>
        </div>
      </div>

      {/* ── GST Certificate Card ── */}
      <div style={{
        background: "linear-gradient(135deg, #fff8e1, #fff3e0)",
        border: "1.5px solid #ffcc80",
        borderRadius: 16,
        padding: "18px 16px",
        marginBottom: 14,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 42, height: 42, minWidth: 42,
            background: config.colors.accent,
            borderRadius: 12,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="receipt_long" style={{ fontSize: 24, color: "#fff" }} />
          </div>
          <div>
            <div style={{
              fontWeight: 800, fontSize: 15, color: "#b45309",
              fontFamily: "'DM Sans', sans-serif",
            }}>
              GST Registration
            </div>
            <div style={{ fontSize: 11, color: "#777", marginTop: 1, fontFamily: "'DM Sans', sans-serif" }}>
              Goods & Services Tax — Government of India
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <LicenseRow label="GSTIN"            value={config.gstNumber} mono copyable accentColor="#b45309" />
          <LicenseRow label="Registered State" value={config.gstState}  accentColor="#b45309" />
          <LicenseRow label="Business Name"    value={config.storeName} accentColor="#b45309" />
          <LicenseRow label="GST Type"         value="Regular Taxpayer" accentColor="#b45309" />
        </div>

        <div style={{
          marginTop: 14,
          background: config.colors.accent,
          borderRadius: 10,
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <Icon name="flag" style={{ fontSize: 20, color: "#fff" }} />
          <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>
            GST Compliant — All prices include applicable taxes
          </span>
        </div>
      </div>

      {/* ── Contact form ── */}
      <Card>
        {sent ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Icon name="check_circle" style={{ fontSize: 52, color: config.colors.primary }} />
            <div style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 800, fontSize: 17, marginTop: 12,
              color: config.colors.primary,
            }}>
              Message Sent!
            </div>
            <div style={{ color: "#aaa", marginTop: 6, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
              We'll get back to you soon.
            </div>
            <button
              onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }}
              style={{
                marginTop: 16,
                background: "none",
                border: `1.5px solid ${config.colors.primary}`,
                color: config.colors.primary,
                borderRadius: 20,
                padding: "7px 20px",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Icon name="refresh" style={{ fontSize: 15, color: config.colors.primary }} />
              Send Another
            </button>
          </div>
        ) : (
          <>
            <div style={{
              fontWeight: 800, fontSize: 15, marginBottom: 14,
              fontFamily: "'DM Sans', sans-serif",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <Icon name="mail_outline" style={{ fontSize: 18, color: config.colors.primary }} />
              Send us a message
            </div>

            <Input label="Name"    placeholder="Your name"         value={form.name}    onChange={update("name")} />
            <Input label="Email"   type="email" placeholder="you@email.com" value={form.email}   onChange={update("email")} />
            <Input label="Message" multiline placeholder="How can we help?" value={form.message} onChange={update("message")} />

            {error && (
              <div style={{
                color: "#dc2626", fontSize: 13,
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: 10, padding: "10px 12px", marginBottom: 12,
                display: "flex", alignItems: "center", gap: 8,
                fontFamily: "'DM Sans', sans-serif",
              }}>
                <Icon name="error_outline" style={{ fontSize: 16, color: "#dc2626" }} />
                {error}
              </div>
            )}

            <Button
              fullWidth
              style={{ fontSize: 15, padding: "14px 0", opacity: loading ? 0.7 : 1 }}
              onClick={handleSend}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'DM Sans', sans-serif" }}>
                {loading ? (
                  <><Icon name="hourglass_top" style={{ fontSize: 18 }} /> Sending...</>
                ) : (
                  <>Send Message <Icon name="send" style={{ fontSize: 16 }} /></>
                )}
              </span>
            </Button>
          </>
        )}
      </Card>

      {/* ── WhatsApp card ── */}
      <Card style={{ background: "#f0fff4", border: "1px solid #b2dfdb" }}>
        <div style={{
          fontWeight: 800, marginBottom: 6, color: config.colors.primary,
          fontFamily: "'DM Sans', sans-serif",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <Icon name="chat" style={{ fontSize: 18, color: config.colors.primary }} />
          Chat on WhatsApp
        </div>
        <div style={{ fontSize: 13, color: "#777", marginBottom: 13, fontFamily: "'DM Sans', sans-serif" }}>
          Get instant support from our team.
        </div>
        <Button
          variant="whatsapp"
          fullWidth
          style={{ fontSize: 15, padding: "14px 0" }}
          onClick={() =>
            window.open(
              `https://wa.me/${config.whatsappNumber}?text=Hi! I need help with ${config.storeName}`,
              "_blank",
            )
          }
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'DM Sans', sans-serif" }}>
            <Icon name="chat_bubble" style={{ fontSize: 18 }} />
            Chat on WhatsApp
          </span>
        </Button>
      </Card>

    </div>
  );
}
